import React, { useState } from "react";
import { UploadCloud } from "lucide-react";

/**
 * A drag and drop file upload zone
 *
 * @param {Object} props
 * @param {Function} props.onFilesSelect - Function called when files are selected
 * @param {boolean} props.disabled - Whether the upload zone is disabled
 * @param {string} props.accept - File types to accept
 * @param {Array} props.acceptedTypes - Array of accepted file types to display
 * @returns {JSX.Element}
 */
const FileUploadZone = ({
  onFilesSelect,
  disabled = false,
  accept = "*",
  acceptedTypes = [],
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    onFilesSelect(droppedFiles);
  };

  const handleFileSelect = (e) => {
    if (disabled) return;

    const selectedFiles = Array.from(e.target.files);
    onFilesSelect(selectedFiles);
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-200
        ${
          isDragging
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        }
        ${disabled ? "opacity-50 pointer-events-none" : ""}
        bg-white`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        multiple
        accept={accept}
        disabled={disabled}
      />
      <div className="text-center">
        <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          Drag and drop files here or click to browse
        </p>
        {acceptedTypes.length > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            Supported files: {acceptedTypes.join(", ")}
          </p>
        )}
      </div>
    </div>
  );
};

export default FileUploadZone;
